import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Billboard } from '@react-three/drei';
import * as THREE from 'three';

// Alphabet hébreu complet
const HEBREW = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ', 'ק', 'ר', 'ש', 'ת'];

// Couleurs — les plus lumineuses réduites de ~25%
const LETTER_COLORS = [
  '#C9A84C',  // or (inchangé)
  '#B8B4AE',  // était #F5F0E8 (proche blanc) → réduit 25%
  '#8A9099',  // était #B8C0CC → réduit 25%
  '#9F787E',  // était #D4A0A8 → réduit 25%
  '#C9A84C',  // or (inchangé)
];

// Zones du corps : [minY, maxY, rx, rz, count]
// Scale -15% (y * 0.85), spiral resserrée (rx/rz * 0.60)
const BODY_ZONES = [
  [1.32, 1.53, 0.07, 0.06, 3],  // tête
  [1.02, 1.32, 0.13, 0.11, 5],  // haut du corps
  [0.68, 1.02, 0.11, 0.08, 6],  // milieu
  [0.34, 0.68, 0.08, 0.07, 5],  // bas
  [0.04, 0.34, 0.06, 0.05, 3],  // pieds
];

function makeLetterTex(letter, color) {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const grd = ctx.createRadialGradient(32, 32, 2, 32, 32, 26);
  grd.addColorStop(0, color + '44');  // halo réduit (était '55')
  grd.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, 64, 64);
  ctx.font = 'bold 42px serif';
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(letter, 32, 34);
  return new THREE.CanvasTexture(canvas);
}

export default function HebrewAvatar({ avatarRef }) {
  const groupRef  = useRef();
  const trail1Ref = useRef();
  const trail2Ref = useRef();
  const letterRefs = useRef([]);
  const timeRef    = useRef(0);
  const trailPos   = useRef({ x: 0, z: 13 });
  const trail2Pos  = useRef({ x: 0, z: 13 });

  const letters = useMemo(() => {
    const result = [];
    let li = 0;
    BODY_ZONES.forEach(([minY, maxY, rx, rz, count]) => {
      for (let i = 0; i < count; i++) {
        const letter = HEBREW[li % HEBREW.length];
        const color  = LETTER_COLORS[li % LETTER_COLORS.length];
        const t      = i / Math.max(count - 1, 1);
        const y      = minY + t * (maxY - minY);
        const angle  = (li / 22) * Math.PI * 4.2;
        const x      = Math.cos(angle) * rx;
        const z      = Math.sin(angle) * rz;
        const tex    = makeLetterTex(letter, color);
        // Tailles variées : mix organique (petites majoritaires, quelques grandes)
        const size   = 0.06 + Math.pow(Math.random(), 1.8) * 0.14;
        result.push({
          letter, color, x, y, z, size, tex,
          phase:      li * 0.80,
          driftPhase: li * 0.37,  // phase de dérive verticale individuelle
        });
        li++;
      }
    });
    return result;
  }, []);

  useFrame((_, delta) => {
    timeRef.current += delta;
    const t  = timeRef.current;
    const av = avatarRef.current;

    // ── Mouvement principal du vortex ──
    if (groupRef.current && av) {
      groupRef.current.position.x += (av.x - groupRef.current.position.x) * 0.18;
      groupRef.current.position.z += (av.z - groupRef.current.position.z) * 0.18;
      groupRef.current.position.y  = Math.sin(t * 0.9) * 0.03;
    }

    // ── Trail 1 : lag moyen ──
    if (trail1Ref.current && groupRef.current) {
      trailPos.current.x += (groupRef.current.position.x - trailPos.current.x) * 0.06;
      trailPos.current.z += (groupRef.current.position.z - trailPos.current.z) * 0.06;
      trail1Ref.current.position.x = trailPos.current.x;
      trail1Ref.current.position.z = trailPos.current.z;
    }

    // ── Trail 2 : lag lent (plus estompé) ──
    if (trail2Ref.current && groupRef.current) {
      trail2Pos.current.x += (groupRef.current.position.x - trail2Pos.current.x) * 0.03;
      trail2Pos.current.z += (groupRef.current.position.z - trail2Pos.current.z) * 0.03;
      trail2Ref.current.position.x = trail2Pos.current.x;
      trail2Ref.current.position.z = trail2Pos.current.z;
    }

    // ── Animation des lettres ──
    letterRefs.current.forEach((ref, i) => {
      if (!ref) return;
      const l = letters[i];

      // Rotation horizontale resserrée (cohésion du vortex)
      ref.position.x = l.x + Math.sin(t * 0.60 + l.phase) * 0.035;
      ref.position.z = l.z + Math.cos(t * 0.50 + l.phase) * 0.030;

      // Dérive verticale douce — "souffle vivant" de chaque lettre
      const drift = Math.sin(t * 0.35 + l.driftPhase) * 0.028        // vague lente
                  + Math.sin(t * 0.72 + l.driftPhase * 1.4) * 0.012; // micro-vibration
      ref.position.y = l.y + drift;
    });
  });

  return (
    <>
      {/* ── Trail 2 — le plus lent, le plus discret ── */}
      <group ref={trail2Ref} position={[0, 0, 13]}>
        <mesh position={[0, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.40, 24]} />
          <meshBasicMaterial color="#C9A84C" transparent opacity={0.020} depthWrite={false} />
        </mesh>
      </group>

      {/* ── Trail 1 ── */}
      <group ref={trail1Ref} position={[0, 0, 13]}>
        <mesh position={[0, 0.015, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.48, 28]} />
          <meshBasicMaterial color="#C9A84C" transparent opacity={0.030} depthWrite={false} />
        </mesh>
      </group>

      {/* ── Vortex principal ── */}
      <group ref={groupRef} position={[0, 0, 13]}>
        {/* Halo au sol — plus doux, légèrement plus petit (-15%), intensité -20% */}
        <mesh position={[0, 0.018, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.55, 32]} />
          <meshBasicMaterial color="#C9A84C" transparent opacity={0.072} depthWrite={false} />
        </mesh>

        {/* Lumière douce — intensité réduite -20% (était 2.2) */}
        <pointLight color="#FFE8A0" intensity={1.76} distance={4.5} decay={2} />

        {/* Lettres hébraïques — le vortex sacré */}
        {letters.map((l, i) => (
          <Billboard key={`letter-${l.char}-${i}`} follow lockX={false} lockY={false} lockZ={false}>
            <mesh
              ref={el => { letterRefs.current[i] = el; }}
              position={[l.x, l.y, l.z]}
            >
              <planeGeometry args={[l.size, l.size]} />
              <meshBasicMaterial
                map={l.tex}
                transparent
                opacity={0.72}
                depthWrite={false}
                side={THREE.DoubleSide}
              />
            </mesh>
          </Billboard>
        ))}

        {/* Sphère fantôme — présence subtile du visiteur */}
        <mesh position={[0, 0.85, 0]}>
          <sphereGeometry args={[0.46, 14, 14]} />
          <meshBasicMaterial color="#C9A84C" transparent opacity={0.032} depthWrite={false} />
        </mesh>
      </group>
    </>
  );
}
